/**
 * Editor pane: title field + body area with a preview/source toggle.
 *
 * Edits are debounced before they reach the sync layer; the draft itself is
 * kept locally so typing is never blocked on a network round-trip.
 *
 * In source (editing) mode a Markdown format toolbar mirrors the macOS
 * `MarkdownFormatToolbar` — see MarkdownFormatToolbar.tsx.
 */
import {
  AlertTriangle,
  ChevronLeft,
  Eye,
  History,
  Link as LinkIcon,
  Paperclip,
  Pencil,
  Sparkles,
  Square,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DebouncedCommit } from '../lib/util/debounce.js';
import type { NoteRecord } from '../lib/sync/dto.js';
import {
  computeFormatEdit,
  type FormatAction,
  type FormatEdit,
} from '../lib/editor/markdownFormatter.js';
import { loadEditorFontSize, nextEditorFontSize, saveEditorFontSize } from '../lib/editor/fontSize.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';
import { MarkdownView } from './MarkdownView.js';
import { MarkdownFormatToolbar } from './MarkdownFormatToolbar.js';

const DEBOUNCE_MS = 600;

export interface EditorDraft {
  title: string;
  content: string;
}

/**
 * AI Organize view for the note being displayed — computed by LibraryScreen
 * so the floating capsule and stream override always describe THIS note, not
 * whatever note the pinned run belongs to.
 */
export interface OrganizeView {
  /** LLM endpoint configured on the server; the capsule is hidden otherwise. */
  available: boolean;
  isStreaming: boolean;
  waitingStatus: string | null;
  errorMessage: string | null;
  canUndo: boolean;
  /** Live rewrite while it belongs to this note; null while waiting/idle. */
  streamedContent: string | null;
  /** Receives the flushed draft (pending edits committed first). */
  onStart: (draft: EditorDraft) => void;
  onStop: () => void;
  onUndo: () => void;
  onDismissError: () => void;
}

/** Selection captured when the Insert Link dialog opens (Mac parity). */
interface LinkDraft {
  selStart: number;
  selEnd: number;
  text: string;
  url: string;
}

interface EditorPaneProps {
  note: NoteRecord | null;
  vaultedNoteIds: Set<string>;
  onChange: (draft: EditorDraft) => void;
  onDelete: () => void;
  onRestore: () => void;
  isTrashed: boolean;
  readOnly?: boolean;
  readOnlyNotice?: React.ReactNode;
  /** Uploads a file and returns the reference to insert into the body. */
  onUpload?: (file: File, insert: (reference: string) => void) => void | Promise<void>;
  /** Opens the version-history dialog for this note. */
  onOpenHistory?: () => void;
  /** Mobile only: pops back to the note list. Omitted on desktop. */
  onBack?: () => void;
  /** Portable reference -> blob URL for decrypted vault attachments. */
  attachmentURLs?: Map<string, string>;
  /** AI Organize controls for this note (floating capsule). */
  organize?: OrganizeView;
}

export function EditorPane({
  note,
  vaultedNoteIds,
  onChange,
  onDelete,
  onRestore,
  isTrashed,
  readOnly = false,
  readOnlyNotice,
  onUpload,
  onOpenHistory,
  onBack,
  attachmentURLs,
  organize,
}: EditorPaneProps) {
  const [mode, setMode] = useState<'preview' | 'source'>('preview');
  const [draft, setDraft] = useState<EditorDraft>({ title: '', content: '' });
  const [fontSize, setFontSize] = useState(() => loadEditorFontSize());
  const [linkDraft, setLinkDraft] = useState<LinkDraft | null>(null);
  // Debounces draft commits, keeping the pending value flushable so a note
  // switch or unmount commits instead of dropping or misplacing it.
  const committer = useRef<DebouncedCommit<EditorDraft> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Guards against echoing the note we are currently displaying back through
  // onChange when the parent re-renders with the same record.
  const loadedId = useRef<string | null>(null);

  // Floating capsule scrollbars (macOS overlay parity). One host wraps the
  // body; the scroller is `.editor-body` in preview mode and the textarea in
  // source mode — only one is mounted at a time, so only one thumb shows.
  const previewScroll = useOverlayScrollbar<HTMLDivElement>();
  const sourceScroll = useOverlayScrollbar<HTMLTextAreaElement>();
  const editorHostRef = useCallback(
    (node: HTMLDivElement | null) => {
      previewScroll.hostRef(node);
      sourceScroll.hostRef(node);
    },
    [previewScroll.hostRef, sourceScroll.hostRef],
  );
  const sourceTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      sourceScroll.scrollerRef(node);
    },
    [sourceScroll.scrollerRef],
  );

  // Font size lives on the root as a CSS variable so the textarea, the
  // preview body and future readers all track it; persisted like the Mac's
  // UserDefaults `editorFontSize`.
  useEffect(() => {
    document.documentElement.style.setProperty('--font-editor-size', `${fontSize}px`);
    saveEditorFontSize(fontSize);
  }, [fontSize]);

  // A config import (Settings → Import from Mac) rewrites the stored size;
  // re-read it so the running editor tracks the new value.
  useEffect(() => {
    const onImported = (): void => {
      const stored = loadEditorFontSize();
      setFontSize((current) => (stored !== current ? stored : current));
    };
    window.addEventListener('jnote:preferences-imported', onImported);
    return () => window.removeEventListener('jnote:preferences-imported', onImported);
  }, []);

  // Draft follows the opened note. Anything still debounced for the previous
  // note is committed first — the timer alone would keep running against the
  // new selection, and clearing it (the old unmount behaviour) ate the last
  // edits whenever the user switched within the debounce window.
  useEffect(() => {
    const hadPending = committer.current?.pending ?? false;
    committer.current?.flush();
    if (!note) {
      loadedId.current = null;
      setDraft({ title: '', content: '' });
      return;
    }
    const switchedNote = loadedId.current !== note.id;
    loadedId.current = note.id;
    // Same note with an edit that just flushed: the draft equals what was
    // committed, so leave it alone. Otherwise resync from the record — this
    // is also how a remote update to the open note becomes visible.
    if (hadPending && !switchedNote) return;
    setDraft({ title: note.title, content: note.content });
  }, [note]);

  // Leaving the editor entirely (e.g. switching to the Vault view) commits
  // the pending edit rather than cancelling it.
  useEffect(() => () => committer.current?.flush(), []);

  function scheduleCommit(next: EditorDraft): void {
    setDraft(next);
    if (readOnly) return;
    committer.current ??= new DebouncedCommit<EditorDraft>(DEBOUNCE_MS);
    committer.current.schedule(next, onChange);
  }

  /**
   * Appends an attachment reference to the body and pushes it through the
   * normal commit path so the change syncs like any other edit.
   */
  function insertReference(reference: string): void {
    const separator = draft.content.length === 0 || draft.content.endsWith('\n') ? '' : '\n';
    const next: EditorDraft = {
      title: draft.title,
      content: `${draft.content}${separator}![${reference.split('/').pop() ?? 'file'}](${reference})\n`,
    };
    scheduleCommit(next);
  }

  /**
   * Applies a precomputed edit to the textarea. `execCommand('insertText')`
   * keeps the browser's native undo stack intact, exactly like the Mac's
   * shouldChangeText/didChangeText pair; the setter fallback covers engines
   * where the command is unavailable.
   */
  function applyEdit(edit: FormatEdit): void {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(edit.start, edit.end);
    const applied = document.execCommand('insertText', false, edit.replacement);
    if (!applied) {
      const valueSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')
        ?.set;
      if (!valueSetter) return;
      valueSetter.call(
        el,
        el.value.slice(0, edit.start) + edit.replacement + el.value.slice(edit.end),
      );
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
    el.setSelectionRange(edit.cursorStart, edit.cursorEnd);
  }

  /** Formats the current textarea selection, like `EditorController.apply`. */
  function applyFormat(action: FormatAction): void {
    const el = textareaRef.current;
    if (!el || readOnly) return;
    applyEdit(computeFormatEdit(el.value, el.selectionStart, el.selectionEnd, action));
  }

  function stepFontSize(direction: 1 | -1): void {
    const next = nextEditorFontSize(fontSize, direction);
    if (next !== null) setFontSize(next);
  }

  /**
   * Starts an AI Organize run for this note. Pending debounced edits are
   * flushed first (the Mac's `saveNow()`), then the current draft is handed
   * to the controller as the run's source and restore snapshot.
   */
  function startOrganize(): void {
    if (!organize || readOnly) return;
    committer.current?.flush();
    organize.onStart({ title: draft.title, content: draft.content });
  }

  /**
   * Toolbar/keyboard shortcuts, mirroring the macOS Format menu:
   * ⌘B/⌘I/⌘⇧X/⌥⌘2/⇧⌘E/⌘K and ⌘⇧+/⌘⇧- for the font ladder.
   */
  function handleSourceKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (!(event.metaKey || event.ctrlKey)) return;
    if (event.shiftKey && (event.key === '+' || event.key === '=')) {
      event.preventDefault();
      stepFontSize(1);
      return;
    }
    if (event.shiftKey && (event.key === '-' || event.key === '_')) {
      event.preventDefault();
      stepFontSize(-1);
      return;
    }
    const key = event.key.toLowerCase();
    if (key === 'b' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      applyFormat({ type: 'bold' });
    } else if (key === 'i' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      applyFormat({ type: 'italic' });
    } else if (key === 'x' && event.shiftKey && !event.altKey) {
      event.preventDefault();
      applyFormat({ type: 'strikethrough' });
    } else if (key === '2' && event.altKey && !event.shiftKey) {
      event.preventDefault();
      applyFormat({ type: 'heading', level: 2 });
    } else if (key === 'e' && event.shiftKey && !event.altKey) {
      event.preventDefault();
      applyFormat({ type: 'inlineCode' });
    } else if (key === 'k' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      openLinkDialog();
    }
  }

  /** Captures the selection for the dialog, exactly like `insertLink` on macOS. */
  function openLinkDialog(): void {
    const el = textareaRef.current;
    if (!el || readOnly) return;
    setLinkDraft({
      selStart: el.selectionStart,
      selEnd: el.selectionEnd,
      text: el.value.slice(el.selectionStart, el.selectionEnd),
      url: '',
    });
  }

  function insertLink(event: React.FormEvent): void {
    event.preventDefault();
    const pending = linkDraft;
    setLinkDraft(null);
    if (!pending) return;
    const url = pending.url.trim();
    if (url.length === 0) return;
    const label = pending.text.trim();
    const text = label.length === 0 ? url : label;
    applyEdit(
      computeFormatEdit(
        textareaRef.current?.value ?? draft.content,
        pending.selStart,
        pending.selEnd,
        { type: 'snippet', text: `[${text}](${url})` },
      ),
    );
  }

  if (!note) {
    return (
      <div className="editor editor-empty">
        <div style={{ color: 'var(--fg-faint)' }}>Select a note, or create one.</div>
      </div>
    );
  }

  // While an organize run streams into THIS note, its body replaces the
  // draft for display and edits are locked. Until the first token arrives the
  // original text stays visible, exactly like the Mac.
  const organizingThisNote = organize?.isStreaming === true;
  const displayedContent =
    organizingThisNote && organize?.streamedContent != null ? organize.streamedContent : draft.content;
  const bodyReadOnly = readOnly || organizingThisNote;

  return (
    <div className="editor">
      <div className="editor-header">
        {onBack && (
          <button type="button" className="editor-back" onClick={onBack} aria-label="Back to list">
            <ChevronLeft size={20} strokeWidth={1.75} />
            <span className="editor-back-label">Notes</span>
          </button>
        )}
        <input
          className="editor-title"
          value={draft.title}
          readOnly={readOnly}
          placeholder="Untitled"
          aria-label="Note title"
          onChange={(event) => scheduleCommit({ ...draft, title: event.target.value })}
        />
        <div className="editor-actions">
          {onOpenHistory && (
            <button
              type="button"
              className="button button-quiet button-small"
              onClick={onOpenHistory}
              title="Version history"
            >
              <History size={13} strokeWidth={1.75} />
              <span className="button-label">History</span>
            </button>
          )}
          {onUpload && !readOnly && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="visually-hidden"
                aria-hidden="true"
                tabIndex={-1}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Reset first so re-picking the same file still fires change.
                  event.target.value = '';
                  if (file) void onUpload(file, insertReference);
                }}
              />
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => fileInputRef.current?.click()}
                title="Attach a file"
              >
                <Paperclip size={13} strokeWidth={1.75} />
                <span className="button-label">Attach</span>
              </button>
            </>
          )}

          <div className="mode-toggle" role="tablist" aria-label="Editor mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'preview'}
              className={mode === 'preview' ? 'mode-tab mode-tab-active' : 'mode-tab'}
              onClick={() => setMode('preview')}
            >
              <Eye size={13} strokeWidth={1.75} />
              Preview
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'source'}
              className={mode === 'source' ? 'mode-tab mode-tab-active' : 'mode-tab'}
              onClick={() => setMode('source')}
            >
              <Pencil size={13} strokeWidth={1.75} />
              Source
            </button>
          </div>

          {isTrashed ? (
            <>
              <button type="button" className="button button-quiet button-small" onClick={onRestore}>
                <Undo2 size={13} strokeWidth={1.75} />
                <span className="button-label">Restore</span>
              </button>
              <button type="button" className="button button-danger button-small" onClick={onDelete}>
                <Trash2 size={13} strokeWidth={1.75} />
                <span className="button-label">Delete forever</span>
              </button>
            </>
          ) : (
            <button type="button" className="button button-danger button-small" onClick={onDelete}>
              <Trash2 size={13} strokeWidth={1.75} />
              <span className="button-label">Delete</span>
            </button>
          )}
        </div>
      </div>

      {readOnlyNotice}

      {organize?.errorMessage && (
        <div className="editor-ai-error" role="alert">
          <AlertTriangle size={13} strokeWidth={1.75} />
          <span>AI Organize failed — {organize.errorMessage}</span>
          <button
            type="button"
            className="editor-ai-error-dismiss"
            onClick={organize.onDismissError}
            aria-label="Dismiss"
          >
            <X size={12} strokeWidth={1.75} />
          </button>
        </div>
      )}

      {/* Format chrome only exists in editing mode, like the macOS
          reading/editing layouts (header showsFormatChrome). */}
      {mode === 'source' && (
        <MarkdownFormatToolbar
          fontSize={fontSize}
          onFontSizeChange={setFontSize}
          enabled={!readOnly}
          onAction={applyFormat}
          onInsertLink={openLinkDialog}
        />
      )}

      <div className="ob-host ob-host-grow" ref={editorHostRef}>
        <div className="editor-body" ref={previewScroll.scrollerRef}>
          {mode === 'preview' ? (
            <MarkdownView
              source={displayedContent}
              vaultedNoteIds={vaultedNoteIds}
              attachmentURLs={attachmentURLs}
            />
          ) : (
            <textarea
              ref={sourceTextareaRef}
              className="editor-source"
              value={displayedContent}
              readOnly={bodyReadOnly}
              spellCheck={false}
              aria-label="Note source"
              onChange={(event) => scheduleCommit({ ...draft, content: event.target.value })}
              onKeyDown={handleSourceKeyDown}
            />
          )}
        </div>
      </div>

      {organize?.available && !readOnly && !isTrashed && (
        <div className="editor-ai-float" role="group" aria-label="AI Organize">
          {organize.isStreaming ? (
            <>
              {organize.waitingStatus && (
                <span className="editor-ai-status">
                  <span className="editor-ai-spinner" aria-hidden="true" />
                  {organize.waitingStatus}
                </span>
              )}
              <button
                type="button"
                className="editor-ai-btn editor-ai-btn-stop"
                onClick={organize.onStop}
                title="Stop AI Organize and restore the original note"
                aria-label="Stop AI Organize"
              >
                <Square size={11} strokeWidth={2} fill="currentColor" stroke="none" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="editor-ai-btn"
                onClick={startOrganize}
                title="AI Organize — reformat this note with the configured LLM"
                aria-label="AI Organize"
              >
                <Sparkles size={14} strokeWidth={1.75} />
              </button>
              {organize.canUndo && (
                <button
                  type="button"
                  className="editor-ai-btn"
                  onClick={organize.onUndo}
                  title="Undo AI Organize and restore the original note"
                  aria-label="Undo AI Organize"
                >
                  <Undo2 size={14} strokeWidth={1.75} />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {linkDraft && (
        <div className="modal-backdrop" onClick={() => setLinkDraft(null)}>
          <form
            className="modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={insertLink}
          >
            <div className="modal-header">
              <LinkIcon size={14} strokeWidth={1.75} />
              <span>Insert Link</span>
            </div>
            <div className="modal-body">
              <p className="modal-text">Enter the link text and destination URL.</p>
              <label className="settings-field">
                <span className="settings-label">Text</span>
                <input
                  className="input"
                  value={linkDraft.text}
                  placeholder="Link text"
                  onChange={(event) =>
                    setLinkDraft((pending) =>
                      pending ? { ...pending, text: event.target.value } : pending,
                    )
                  }
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">URL</span>
                <input
                  className="input"
                  value={linkDraft.url}
                  placeholder="https://"
                  autoFocus
                  onChange={(event) =>
                    setLinkDraft((pending) =>
                      pending ? { ...pending, url: event.target.value } : pending,
                    )
                  }
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="button button-quiet"
                  onClick={() => setLinkDraft(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="button">
                  Insert
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
